import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { ConstantsService, SessionService, LookupsService, LoggerService,
  RequestTypesEnum, StepsIdEnum, StepsRouteEnum, EnvironmentService } from '@app/base/core';
import { MultistepsService } from '@app/modules/applicant/base/services';
import { Location } from '@angular/common';
import { SubscriptionLike, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { getFormValidators } from '@app/base/shared';

@Component({
  selector: 'app-applicant',
  templateUrl: './applicant.component.html',
  styleUrls: ['./applicant.component.css'],
  providers: [MultistepsService]
  // Provide the service in outlet component scope and not module scope, when user leave applicant route outlet component gets destroyed
  // the service instance will get destroyed as well so we don't have to reset state manually!
})
export class ApplicantComponent implements OnInit, OnDestroy {
  activeCode$: Subscription;
  formValidation: any[];
  lookupsLoaded = false;
  stepsIdEnum: typeof StepsIdEnum = StepsIdEnum;
  requestTypeEnum: typeof RequestTypesEnum = RequestTypesEnum;
  locationSubscription: SubscriptionLike;

  constructor(private router: Router,
    public lookupSvc: LookupsService,
    private route: ActivatedRoute,
    private location: Location,
    private logger: LoggerService,
    public environmentSvc: EnvironmentService,
    public constantsSvc: ConstantsService,
    public sessionSvc: SessionService,
    public wizardSvc: MultistepsService) {
    this.subscribeRouteEvents();
  }

  ngOnInit() {
    // Changed to observable to eliminate undefined issues
    this.activeCode$ = this.sessionSvc.activeLdcDetails.subscribe(res => {
      if(res && res.code) {
        this.lookupSvc.loadBcaLookups(this.wizardSvc.paramRequestType, res.code);
      }

      /* istanbul ignore next */
      const sub = this.lookupSvc.commonLookupsLoaded$.subscribe(val => {
        if (sub && val) { sub.unsubscribe(); }
        this.lookupsLoaded = val;
      });

      if (!this.environmentSvc.isSoCoProduction) {
        this.wizardSvc.steps.forEach(step => {
          step.form.valueChanges.subscribe(val => {
            this.formValidation = getFormValidators(this.wizardSvc.getActiveStep.form);
          });
        });
      }
    });
  }

  ngOnDestroy() {
    this.locationSubscription.unsubscribe();
    this.activeCode$.unsubscribe();
  }

 /* istanbul ignore next */
  subscribeRouteEvents() {
    // Detect and resolve parameterized Request Type
    this.route.params.subscribe(params => {
      if (params['type']) { // If we received type, then try to match it with a known Type enum
        const paramType = String(params['type']).trim().toUpperCase();
        // Ensure that incoming query param is matching one of known Types in RequestTypes enum before stroing it.
        if (Object.keys(RequestTypesEnum).map(e => RequestTypesEnum[e].toUpperCase()).indexOf(paramType) >= 0) {
          this.wizardSvc.paramRequestType = paramType; // Type passed in route is a known value store it.
          this.wizardSvc.paramRequestTypeResolved(); // Param Request Type resolved infrom service to build the forms.

          const resolvedStep = this.wizardSvc.steps.find(x =>
            x.route.toUpperCase() === this.route.snapshot.firstChild.url[0].path.toUpperCase()).route.toUpperCase();
          if (this.wizardSvc.getActiveStep.route.toUpperCase() !== resolvedStep) { this.navigateToDefaultStep(); }
        } else { this.navigateToDefaultStep(); } // Value passed in :type is unknown request type, route to default request type.
      } else { this.navigateToDefaultStep(); } // No Type passed in route, route to default request type.
    });

    if (!this.environmentSvc.isSoCoProduction) {
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: any) => {
        this.formValidation = getFormValidators(this.wizardSvc.getActiveStep.form);
      });
    }

    // Detect and act on route changes through platform navigation buttons, e.g. briowser back and forward
    this.locationSubscription = this.location.subscribe(event => {
      const resolvedStep = event.url.substr(event.url.lastIndexOf('/') + 1).toUpperCase();
      // Ensure that step is a known step
      if (Object.keys(StepsRouteEnum).map(e => StepsRouteEnum[e].toUpperCase()).indexOf(resolvedStep) >= 0) {
        this.wizardSvc.goToStep(StepsIdEnum[resolvedStep]);
      } else { this.navigateToDefaultStep(); }  // Couldn't recognize the step, route to default
    });
  }

  navigateToDefaultStep() {
    this.wizardSvc.goToStep(this.stepsIdEnum.LOCATION);
  }
}
